import React, { useMemo, useState } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Box, Stack, Typography,
  TextField, MenuItem, IconButton, Button, Chip, Tooltip, InputAdornment, Divider,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import HistoryIcon from "@mui/icons-material/History";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { IEmailTemplate, IEmailTemplateCategoryOption } from "../../Api/emailTemplatesApi";

const NAVY = "#0F172A";
const CHAMPAGNE = "#C9A961";
const CHAMPAGNE_PALE = "#FBF7EE";
const HAIRLINE = "rgba(10,14,26,0.06)";

export interface TemplateSelectorProps {
  open: boolean;
  templates: IEmailTemplate[];
  categories: IEmailTemplateCategoryOption[];
  favorites: number[];
  recents: number[];
  /** Toggle a template's favorite state in the library hook. */
  onToggleFavorite: (id: number) => void;
  onApply: (template: IEmailTemplate) => void;
  onClose: () => void;
  /** Optional inline preview launcher — when present, each row shows
   *  an eye icon that opens the parent's preview surface. */
  onPreview?: (template: IEmailTemplate) => void;
  title?: string;
}

/**
 * Searchable template picker used by both the Email Hub Reply modal
 * and the Lead → Send Email panel. State-pure: the consumer hook
 * owns favorites/recents — this component is a presentation surface
 * over a fixed list.
 */
const TemplateSelector: React.FC<TemplateSelectorProps> = ({
  open, templates, categories, favorites, recents,
  onToggleFavorite, onApply, onClose, onPreview, title,
}) => {
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState<string>("all");
  const [view, setView] = useState<"all" | "favorites" | "recent">("all");

  const filtered = useMemo(() => {
    let list = templates;
    if (category !== "all") list = list.filter(t => t.category === category);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter(t =>
        t.name.toLowerCase().includes(q)
        || (t.subject || "").toLowerCase().includes(q)
        || (t.description || "").toLowerCase().includes(q)
        || (t.slug || "").toLowerCase().includes(q)
      );
    }
    if (view === "favorites") {
      list = list.filter(t => favorites.includes(t.id));
    } else if (view === "recent") {
      const order = new Map(recents.map((id, i) => [id, i]));
      list = list.filter(t => order.has(t.id))
                 .sort((a, b) => (order.get(a.id) ?? 0) - (order.get(b.id) ?? 0));
    }
    return list;
  }, [templates, category, search, view, favorites, recents]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pr: 5 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Box>
            <Typography sx={{ fontSize: 17, fontWeight: 700, color: NAVY }}>
              {title || "Use a Template"}
            </Typography>
            <Typography sx={{ fontSize: 11.5, color: "#64748b" }}>
              Pick a template — subject + body apply with variables resolved.
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0 }}>
        {/* ── Filter bar ─────────────────────────────────────── */}
        <Box sx={{ p: 1.5, borderBottom: `1px solid ${HAIRLINE}`, bgcolor: "#fafbfc" }}>
          <Stack direction={{ xs: "column", md: "row" }} gap={1} alignItems={{ md: "center" }}>
            <TextField
              size="small"
              placeholder="Search templates…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{ flex: 1, minWidth: 220 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 18, color: "#94a3b8" }} />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              size="small" select label="Category"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              sx={{ minWidth: 180 }}
            >
              <MenuItem value="all">All Categories</MenuItem>
              {categories.map(c => (
                <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>
              ))}
            </TextField>
            <Stack direction="row" gap={0.4}>
              {(["all", "favorites", "recent"] as const).map(v => {
                const active = view === v;
                const label = v === "all" ? "All" : v === "favorites" ? `Favorites${favorites.length ? ` (${favorites.length})` : ""}` : "Recent";
                return (
                  <Box
                    key={v}
                    role="button"
                    onClick={() => setView(v)}
                    sx={{
                      cursor: "pointer", userSelect: "none",
                      px: 1.1, py: 0.4, borderRadius: "8px",
                      fontSize: 11, fontWeight: active ? 700 : 600,
                      color: active ? NAVY : "#94a3b8",
                      bgcolor: active ? "#fff" : "transparent",
                      border: active ? `1px solid ${NAVY}` : `1px solid transparent`,
                    }}
                  >
                    {label}
                  </Box>
                );
              })}
            </Stack>
          </Stack>
        </Box>

        {/* ── List ─────────────────────────────────────────── */}
        <Box sx={{ maxHeight: "55vh", overflow: "auto" }}>
          {filtered.length === 0 ? (
            <Box sx={{ py: 6, textAlign: "center" }}>
              <Typography sx={{ fontSize: 13, color: "#94a3b8", fontStyle: "italic" }}>
                {view === "favorites"
                  ? "No favorite templates yet. Click the star on a template to favorite it."
                  : view === "recent"
                  ? "No recently used templates yet."
                  : "No templates match those filters."}
              </Typography>
            </Box>
          ) : (
            filtered.map((t, i) => {
              const fav = favorites.includes(t.id);
              return (
                <Box
                  key={t.id}
                  onClick={() => onApply(t)}
                  sx={{
                    cursor: "pointer",
                    px: 2, py: 1.2,
                    borderBottom: i === filtered.length - 1 ? "none" : `1px solid ${HAIRLINE}`,
                    transition: "background 150ms",
                    "&:hover": { bgcolor: CHAMPAGNE_PALE },
                  }}
                >
                  <Stack direction="row" alignItems="flex-start" gap={1.2}>
                    {/* Star — toggles favorite without dismissing modal */}
                    <Tooltip title={fav ? "Remove from favorites" : "Add to favorites"} arrow>
                      <IconButton
                        size="small"
                        onClick={(e) => { e.stopPropagation(); onToggleFavorite(t.id); }}
                        sx={{ mt: -0.2 }}
                      >
                        {fav
                          ? <StarIcon sx={{ fontSize: 16, color: CHAMPAGNE }} />
                          : <StarBorderIcon sx={{ fontSize: 16, color: "#94a3b8" }} />}
                      </IconButton>
                    </Tooltip>

                    <Box flex={1} minWidth={0}>
                      <Stack direction="row" alignItems="center" gap={0.7} flexWrap="wrap" useFlexGap>
                        <Typography sx={{ fontSize: 13, fontWeight: 700, color: NAVY }} noWrap>
                          {t.name}
                        </Typography>
                        {t.is_default && (
                          <Chip size="small" label="Default" sx={{ height: 18, fontSize: 9.5, fontWeight: 700, bgcolor: CHAMPAGNE_PALE, color: "#A8893E" }} />
                        )}
                        {t.category_label && (
                          <Chip size="small" label={t.category_label} sx={{ height: 18, fontSize: 9.5, fontWeight: 600, bgcolor: "#f1f5f9", color: "#475569" }} />
                        )}
                        {recents.includes(t.id) && (
                          <Stack direction="row" alignItems="center" gap={0.2}>
                            <HistoryIcon sx={{ fontSize: 12, color: "#94a3b8" }} />
                            <Typography sx={{ fontSize: 9.5, color: "#94a3b8", fontWeight: 600 }}>
                              recent
                            </Typography>
                          </Stack>
                        )}
                      </Stack>
                      <Typography sx={{ fontSize: 11.5, color: "#475569", mt: 0.2 }} noWrap>
                        {t.subject}
                      </Typography>
                      {t.description && (
                        <Typography sx={{ fontSize: 10.5, color: "#94a3b8", mt: 0.15 }} noWrap>
                          {t.description}
                        </Typography>
                      )}
                    </Box>

                    {onPreview && (
                      <Tooltip title="Preview" arrow>
                        <IconButton
                          size="small"
                          onClick={(e) => { e.stopPropagation(); onPreview(t); }}
                        >
                          <VisibilityOutlinedIcon sx={{ fontSize: 16, color: "#64748b" }} />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Stack>
                </Box>
              );
            })
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Button onClick={onClose} sx={{ textTransform: "none" }}>Cancel</Button>
      </DialogActions>
    </Dialog>
  );
};

export default TemplateSelector;
